/* eslint-disable @typescript-eslint/no-unused-vars */
import { Message, useMessage, useMessageGetter } from '@messageformat/react';
import { Breakpoint, BreakpointListener, Breakpoints, Tab, Tabs } from '@op/opds.opux';
import { color, space } from '@op/opds.tokens';
import { ReactElement, useMemo, useState, useRef } from 'react';
import parseMsgContent from '../../util/parseMsgContent';
import CourseContent from '../CourseContent';
import ProfileColumns from './ProfileColumns';
import { ReactComponent as Image057 } from '../../images/OP_symbolikuvitus_128_057.svg';
import { ReactComponent as Image058 } from '../../images/OP_symbolikuvitus_128_058.svg';
import { ReactComponent as Image059 } from '../../images/OP_symbolikuvitus_128_059.svg';
import { ReactComponent as Image060 } from '../../images/OP_symbolikuvitus_128_060.svg';
import { ReactComponent as Image120 } from '../../images/OP_symbolikuvitus_96_120.svg';
import ResultTile from '../Course7/TaskOne/ResultTile';
import { investmentProfiles } from '../Course7/TaskOne/taskoneoptions';
import { ContentBlock } from '../../interfaces/ContentBlock';
import ReturnToTop from '../ReturnToTop';

type Content = {
  mobile: boolean;
};

interface Props {
  isCourse7?: boolean;
}

const getMessageContent = (
  isCourse7: boolean,
  course5Message: string,
  course7Message: string,
): string => {
  return isCourse7 ? course7Message : course5Message;
};

const InvestorProfiles = (props: Props): ReactElement => {
  const { isCourse7 = false } = props;
  const getCourseTexts = useMessageGetter('course5');
  const [breakpoint, setBreakpoint] = useState<Breakpoint | null>(null);
  const content = breakpoint?.content ? (breakpoint.content as Content) : null;

  const selfIntro = getMessageContent(
    isCourse7,
    'course5.profileIntro.pr1',
    'course7.profileIntro.pr1',
  );
  const careIntro = getMessageContent(
    isCourse7,
    'course5.profileIntro.pr2',
    'course7.profileIntro.pr2',
  );
  const diffIntro = getMessageContent(
    isCourse7,
    'course5.profileIntro.pr3',
    'course7.profileIntro.pr3',
  );
  const prepIntro = getMessageContent(
    isCourse7,
    'course5.profileIntro.pr4',
    'course7.profileIntro.pr4',
  );
  const safeIntro = getMessageContent(
    isCourse7,
    'course5.profileIntro.pr5',
    'course7.profileIntro.pr5',
  );

  const selfSolution = useMessage('course5.profileSolutions.s1');
  const careSolution = useMessage('course5.profileSolutions.s2');
  const diffSolution = useMessage('course5.profileSolutions.s3');
  const prepSolution = useMessage('course5.profileSolutions.s4');
  const safeSolution = useMessage('course5.profileSolutions.s5');
  const firstProfile = useMessage('course7.exemplaryInvestors.firstProfile');
  const secondProfile = useMessage('course7.exemplaryInvestors.secondProfile');
  const thirdProfile = useMessage('course7.exemplaryInvestors.thirdProfile');
  const fourthProfile = useMessage('course7.exemplaryInvestors.fourthProfile');
  const fifthProfile = useMessage('course7.exemplaryInvestors.fifthProfile');

  const selfOne = parseMsgContent(useMessage(selfIntro));
  const selfTwo = parseMsgContent(selfSolution);
  const careOne = parseMsgContent(useMessage(careIntro));
  const careTwo = parseMsgContent(careSolution);
  const diffOne = parseMsgContent(useMessage(diffIntro));
  const diffTwo = parseMsgContent(diffSolution);
  const prepOne = parseMsgContent(useMessage(prepIntro));
  const prepTwo = parseMsgContent(prepSolution);
  const safeOne = parseMsgContent(useMessage(safeIntro));
  const safeTwo = parseMsgContent(safeSolution);

  const breakpoints: Breakpoints = useMemo(() => {
    return [
      { from: 0, to: 767, content: { mobile: true } },
      {
        from: 768,
        to: Number.MAX_SAFE_INTEGER,
        content: { mobile: false },
      },
    ];
  }, []);

  const renderProfileContent = (
    title: string,
    profileNumber: number,
    contentData: ContentBlock[],
  ): ReactElement =>
    isCourse7 ? (
      <ResultTile title={title} data={investmentProfiles[profileNumber].data} isWide />
    ) : (
      <CourseContent content={contentData} headingAs="h4" />
    );

  const targetRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<{ [key: string]: number }>({
    first_div: 0,
  });

  const handleTabChange = (divId: string, index: number): void => {
    setActiveTab((prev) => ({
      ...prev,
      [divId]: index,
    }));
  };

  return (
    <BreakpointListener breakpoints={breakpoints} onChange={setBreakpoint}>
      {isCourse7 && (
        <h2
          className="opds-styles-v1-text-hero-small"
          style={{ textAlign: 'center', marginTop: space.xxl, marginBottom: space.large }}
        >
          <Message id="course5.profile_title" />
        </h2>
      )}
      {content?.mobile ? (
        <div ref={targetRef}>
          <Image059
            className="investoracademy-small-svg investoracademy-profile-svg"
            aria-hidden="true"
          />
          <CourseContent
            content={selfOne}
            headingStyle={{ color: color.text.highlight.large }}
          />
          <ProfileColumns
            profileText="course5.investorColumn.body.b3"
            timePeriodText="course5.investmentTime.body.b2"
            activityText="course5.activityLevel.body.b1"
          />
          {renderProfileContent(firstProfile, 0, selfTwo)}

          <Image058
            className="investoracademy-small-svg investoracademy-profile-svg"
            aria-hidden="true"
          />
          <CourseContent
            content={careOne}
            headingStyle={{ color: color.text.highlight.large }}
          />
          <ProfileColumns
            profileText="course5.investorColumn.body.b1"
            timePeriodText="course5.investmentTime.body.b2"
            activityText="course5.activityLevel.body.b2"
          />
          {renderProfileContent(secondProfile, 1, careTwo)}

          <Image060
            className="investoracademy-small-svg investoracademy-profile-svg"
            aria-hidden="true"
          />
          <CourseContent
            content={diffOne}
            headingStyle={{ color: color.text.highlight.large }}
          />
          <ProfileColumns
            profileText="course5.investorColumn.body.b1"
            timePeriodText="course5.investmentTime.body.b3"
            activityText="course5.activityLevel.body.b1"
          />
          {renderProfileContent(thirdProfile, 2, diffTwo)}

          <Image120
            className="investoracademy-small-svg investoracademy-profile-svg"
            aria-hidden="true"
          />
          <CourseContent
            content={prepOne}
            headingStyle={{ color: color.text.highlight.large }}
          />
          <ProfileColumns
            profileText="course5.investorColumn.body.b2"
            timePeriodText="course5.investmentTime.body.b1"
            activityText="course5.activityLevel.body.b2"
          />
          {renderProfileContent(fourthProfile, 3, prepTwo)}

          <Image057
            className="investoracademy-small-svg investoracademy-profile-svg"
            aria-hidden="true"
          />
          <CourseContent
            content={safeOne}
            headingStyle={{ color: color.text.highlight.large }}
          />
          <ProfileColumns
            profileText="course5.investorColumn.body.b2"
            timePeriodText="course5.investmentTime.body.b4"
            activityText="course5.activityLevel.body.b3"
          />
          {renderProfileContent(fifthProfile, 4, safeTwo)}
        </div>
      ) : (
        <div ref={targetRef}>
          <Tabs
            small
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '1 0 0',
            }}
            active={activeTab.first_div}
            onChange={(event, index) => handleTabChange('first_div', index)}
          >
            <Tab
              id="first_tab"
              className="investoracademy-course-tab"
              label={getCourseTexts('investor_profiles.selfdirected')}
            >
              <Image059 className="investoracademy-small-svg" aria-hidden="true" />
              <CourseContent
                content={selfOne}
                headingStyle={{ color: color.text.highlight.large }}
              />
              <ProfileColumns
                profileText="course5.investorColumn.body.b3"
                timePeriodText="course5.investmentTime.body.b2"
                activityText="course5.activityLevel.body.b1"
              />
              {renderProfileContent(firstProfile, 0, selfTwo)}
            </Tab>
            <Tab
              id="second_tab"
              className="investoracademy-course-tab"
              label={getCourseTexts('investor_profiles.carefree')}
            >
              <Image058 className="investoracademy-small-svg" aria-hidden="true" />
              <CourseContent
                content={careOne}
                headingStyle={{ color: color.text.highlight.large }}
              />
              <ProfileColumns
                profileText="course5.investorColumn.body.b1"
                timePeriodText="course5.investmentTime.body.b2"
                activityText="course5.activityLevel.body.b2"
              />
              {renderProfileContent(secondProfile, 1, careTwo)}
            </Tab>
            <Tab
              id="third_tab"
              className="investoracademy-course-tab"
              label={getCourseTexts('investor_profiles.diff')}
            >
              <Image060 className="investoracademy-small-svg" aria-hidden="true" />
              <CourseContent
                content={diffOne}
                headingStyle={{ color: color.text.highlight.large }}
              />
              <ProfileColumns
                profileText="course5.investorColumn.body.b1"
                timePeriodText="course5.investmentTime.body.b3"
                activityText="course5.activityLevel.body.b1"
              />
              {renderProfileContent(thirdProfile, 2, diffTwo)}
            </Tab>
            <Tab
              id="fourth_tab"
              className="investoracademy-course-tab"
              label={getCourseTexts('investor_profiles.prep')}
            >
              <Image120 className="investoracademy-small-svg" aria-hidden="true" />
              <CourseContent
                content={prepOne}
                headingStyle={{ color: color.text.highlight.large }}
              />
              <ProfileColumns
                profileText="course5.investorColumn.body.b2"
                timePeriodText="course5.investmentTime.body.b1"
                activityText="course5.activityLevel.body.b2"
              />
              {renderProfileContent(fourthProfile, 3, prepTwo)}
            </Tab>
            <Tab
              id="fifth_tab"
              className="investoracademy-course-tab"
              label={getCourseTexts('investor_profiles.safe')}
            >
              <Image057 className="investoracademy-small-svg" aria-hidden="true" />
              <CourseContent
                content={safeOne}
                headingStyle={{ color: color.text.highlight.large }}
              />
              <ProfileColumns
                profileText="course5.investorColumn.body.b2"
                timePeriodText="course5.investmentTime.body.b4"
                activityText="course5.activityLevel.body.b3"
              />
              {renderProfileContent(fifthProfile, 4, safeTwo)}
            </Tab>
          </Tabs>
        </div>
      )}
      <ReturnToTop
        msgId="return_to_tabs"
        targetRef={targetRef}
        index={activeTab.first_div}
      />
    </BreakpointListener>
  );
};

export default InvestorProfiles;
